// Which of a kanji's readings the word in front of you is using.
//
// THE QUESTION MYN-176 LEFT OPEN. The hint panel names a kanji's on and kun readings, which is
// the answer to "why is this read three ways" — and on a card showing 食べる it lists た.べる,
// く.う, く.らう and ショク, three of which are about some other word entirely. The learner is
// left holding a dictionary entry when what they wanted was this word.
//
// NOT BY LOOKING THE READING UP INSIDE THE KANA. That rule cannot keep the readings that matter
// most: 自分 is じぶん and 自 is ジ, a single mora, and a one-mora string appears inside half the
// vocabulary by accident — so a substring rule has to discard exactly the short on readings that
// compounds are built from. Measured over the Japanese vocabulary it gets 76% of kanji.
//
// BY POSITION INSTEAD. A word's kana is its characters said in order, so read the word AS its
// characters: each kana in the word stands for itself, each kanji stands for one of its readings,
// and only a walk that consumes the whole reading with nothing left over is an answer. じ is then
// unambiguous in じぶん, because it has to be the FIRST mora and 分 has to account for the rest.
// That reads 1,452 of 1,507 kanji words end to end, and gives 2,208 of 2,311 kanji occurrences
// their own reading (95.5%).
//
// AND WHAT DOES NOT ALIGN IS WORTH KNOWING. The words left over are 今日, 明日, 昨日, 友達,
// 息子, 部屋, 大人 — words that are not read from their kanji at all. A learner who has just been
// told that a kanji is read three ways is precisely the person who should hear that this word is
// read none of them, so the app says so rather than marking nothing and looking broken.

import type { KanjiReadings } from './types.js';

/** Katakana to hiragana, so an on reading can be compared with the kana a word is read in. */
export const toHiragana = (text: string): string =>
  text.replace(/[ァ-ヶ]/gu, (c) => String.fromCodePoint((c.codePointAt(0) ?? 0) - 0x60));

/**
 * The first mora voiced: か→が, は→ば. Rendaku, the sound change that happens to the second half
 * of a compound — 仕事 is しごと, and 事 is read こと everywhere it stands alone.
 */
const VOICED: Record<string, string> = {
  か: 'が', き: 'ぎ', く: 'ぐ', け: 'げ', こ: 'ご',
  さ: 'ざ', し: 'じ', す: 'ず', せ: 'ぜ', そ: 'ぞ',
  た: 'だ', ち: 'ぢ', つ: 'づ', て: 'で', と: 'ど',
  は: 'ば', ひ: 'び', ふ: 'ぶ', へ: 'べ', ほ: 'ぼ',
};

/** The other thing that happens to は in a compound: 一本 is いっぽん, not いっほん. */
const HALF: Record<string, string> = { は: 'ぱ', ひ: 'ぴ', ふ: 'ぷ', へ: 'ぺ', ほ: 'ぽ' };

/**
 * The shapes one reading can take inside a word.
 *
 * The dictionary form first, then the two regular alterations a compound makes: the first mora
 * voices (rendaku) and a final く, き, ち or つ clips to っ (gemination — 学校 is がっこう, from
 * がく). Both are rules rather than exceptions, and without them 仕事 and 学校 read as words that
 * are not read from their kanji, which is the one thing this file must not say wrongly.
 *
 * The okurigana goes: KANJIDIC writes た.べる, and べる is kana that the WORD already carries, so
 * 食 accounts for た and the walk matches べる against the word's own characters.
 */
export function saidAs(reading: string): string[] {
  const stem = toHiragana(reading.split('.')[0] ?? '');
  const forms = new Set<string>();
  if (!stem) return [];
  forms.add(stem);
  const first = stem[0] ?? '';
  if (VOICED[first]) forms.add(VOICED[first] + stem.slice(1));
  if (HALF[first]) forms.add(HALF[first] + stem.slice(1));
  if (/[くきちつ]$/u.test(stem)) forms.add(`${stem.slice(0, -1)}っ`);
  return [...forms];
}

/** One character of a word, and the reading it turned out to be saying. */
export interface ReadingInUse {
  /** The reading as the dictionary writes it, okurigana dot and all: `た.べる`, `セイ`. */
  reading: string;
  /** How it is said HERE, which is not always how it is written: 事 says ごと in 仕事. */
  said: string;
}

/**
 * Read `word` as `kana`, and say which reading each character took.
 *
 * Returns one entry per character of the word — null for a character that is not a kanji with
 * readings, or that stands for itself — or **null for the whole word** when its kana cannot be
 * read from its characters at all. That second null is a finding, not a failure: see the note at
 * the top of this file.
 *
 * @param readingsOf answers with a character's readings, or null if it has none. The app hands
 *   this the hints it already loaded; nothing here reads a file or knows where hints come from.
 */
export function readingsUsedBy(
  word: string,
  kana: string,
  readingsOf: (char: string) => KanjiReadings | null,
): (ReadingInUse | null)[] | null {
  const chars = [...word];
  const said = toHiragana(kana.replace(/\s+/gu, ''));
  if (!said) return null;

  // Depth first, and the FIRST complete walk wins. Where two readings could both account for the
  // same kana the choice is arbitrary — but it is arbitrary between two readings that are both
  // being said, which is a far smaller claim than picking one out of a list of six.
  const walk = (i: number, at: number, found: (ReadingInUse | null)[]): (ReadingInUse | null)[] | null => {
    if (i === chars.length) return at === said.length ? found : null;
    const char = chars[i] ?? '';
    const readings = readingsOf(char);
    if (!readings || (!readings.on.length && !readings.kun.length)) {
      // Kana in the word itself — the べる of 食べる, the の of 私の — stands for itself. Folded
      // to hiragana on both sides so a word written with katakana still reads.
      const plain = toHiragana(char);
      return said.startsWith(plain, at) ? walk(i + 1, at + plain.length, [...found, null]) : null;
    }
    for (const reading of [...readings.on, ...readings.kun]) {
      for (const form of saidAs(reading)) {
        if (!said.startsWith(form, at)) continue;
        const rest = walk(i + 1, at + form.length, [...found, { reading, said: form }]);
        if (rest) return rest;
      }
    }
    return null;
  };
  return walk(0, 0, []);
}
